import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Wrench, Plus, Trash2, Star, FileCheck } from "lucide-react";
import { STATUS_COLOR } from "./_helpers";

const SERVICE_TYPES = ["free", "paid", "warranty", "running_repair", "accident"];

export default function JobCardsTab() {
  const [rows, setRows] = useState([]);
  const [csi, setCsi] = useState(null);
  const [open, setOpen] = useState(false);
  const emptyForm = { customer: { name: "", mobile: "" }, vin: "", service_type: "paid",
                       complaints: [""], service_date: today(), odometer_in: 0, labour_charges: 0,
                       parts_used: [], remarks: "" };
  const [form, setForm] = useState(emptyForm);
  const [csiOpen, setCsiOpen] = useState(null);
  const [csiForm, setCsiForm] = useState({ csi_score: 5, nps_score: 9, feedback: "" });

  const load = async () => {
    const [j, c] = await Promise.all([api.get("/dealership/job-cards"), api.get("/dealership/csi/summary")]);
    setRows(j.data); setCsi(c.data);
  };
  useEffect(() => { load(); }, []);

  const partsTotal = useMemo(() => form.parts_used.reduce((s, p) => s + Number(p.qty || 0) * Number(p.unit_price || 0), 0), [form.parts_used]);
  const totalAmount = partsTotal + Number(form.labour_charges || 0);

  const save = async () => {
    if (!form.customer.name || !form.vin) return toast.error("Customer name and VIN are required");
    try {
      const payload = { ...form,
                          vin: form.vin.toUpperCase(),
                          complaints: form.complaints.filter((c) => c.trim()),
                          parts_used: form.parts_used.filter((p) => p.name).map((p) => ({
                             ...p, total: Number(p.qty || 0) * Number(p.unit_price || 0),
                          })) };
      const r = await api.post("/dealership/job-cards", payload);
      toast.success(`Job Card ${r.data.job_card_number} opened · ${fmtINR(r.data.total_amount)}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const close = async (jc) => {
    try { await api.post(`/dealership/job-cards/${jc.id}/close`); toast.success("Closed"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  // Iter-154 · Convert closed Job Card → posted Sales Invoice
  const invoice = async (jc) => {
    if (!window.confirm(`Generate a service invoice for JC ${jc.job_card_number} (${fmtINR(jc.total_amount)})? A customer party will be created if missing.`)) return;
    try {
      const r = await api.post(`/dealership/job-cards/${jc.id}/convert-to-invoice`);
      toast.success(`Invoice ${r.data.invoice?.invoice_number} posted · ${fmtINR(r.data.invoice?.grand_total || 0)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openCSI = (jc) => { setCsiOpen(jc); setCsiForm({ csi_score: 5, nps_score: 9, feedback: "" }); };
  const runCSI = async () => {
    try {
      await api.post("/dealership/csi", {
        context_type: "service", job_card_id: csiOpen.id,
        customer_mobile: csiOpen.customer?.mobile || "",
        csi_score: csiForm.csi_score, nps_score: csiForm.nps_score, feedback: csiForm.feedback,
      });
      toast.success("CSI response captured");
      setCsiOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="jobcards-tab">
      {csi && csi.total_responses > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="card-sharp p-4" data-testid="csi-total"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">CSI responses</div><div className="font-heading text-2xl mt-1 tabular-nums">{csi.total_responses}</div></div>
          <div className="card-sharp p-4" data-testid="csi-avg"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Avg CSI (/5)</div><div className="font-heading text-2xl mt-1 tabular-nums">{csi.avg_csi}</div></div>
          <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">NPS</div><div className={`font-heading text-2xl mt-1 tabular-nums ${csi.nps_score >= 50 ? "text-emerald-600" : csi.nps_score >= 0 ? "text-amber-500" : "text-red-500"}`}>{csi.nps_score}</div></div>
          <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Promoters · Detractors</div><div className="font-heading text-sm mt-1 tabular-nums">{csi.promoters_pct}% · {csi.detractors_pct}%</div></div>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-jc-btn">
          <Wrench className="w-3.5 h-3.5" /> New Job Card
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>JC#</th><th>Customer</th><th>VIN</th><th>Service Type</th><th>Date</th><th className="text-right">Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No job cards yet.</td></tr>}
            {rows.map((j) => (
              <tr key={j.id} data-testid={`jc-row-${j.job_card_number}`}>
                <td className="font-mono text-xs">{j.job_card_number}</td>
                <td className="text-xs">{j.customer?.name}<br/><span className="text-[10px] font-mono text-muted-foreground">{j.customer?.mobile}</span></td>
                <td className="font-mono text-[10px]">{j.vin}</td>
                <td className="text-[10px] font-mono uppercase">{j.service_type.replace("_", " ")}</td>
                <td className="text-xs">{fmtDate(j.service_date)}</td>
                <td className="text-right tabular-nums">{fmtINR(j.total_amount)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[j.status] || ""}`}>{j.status}</span></td>
                <td className="text-right space-x-1">
                  {j.status !== "closed" && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => close(j)} data-testid={`close-jc-${j.job_card_number}`}>Close</Button>}
                  {j.status === "closed" && !j.invoice_id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-blue-700"
                            onClick={() => invoice(j)} data-testid={`invoice-jc-${j.job_card_number}`}>
                      <FileCheck className="w-3 h-3" /> Invoice
                    </Button>
                  )}
                  {j.invoice_number && (
                    <span className="text-[10px] font-mono uppercase text-emerald-600" title="Linked sales invoice">
                      ✓ {j.invoice_number}
                    </span>
                  )}
                  {j.status === "closed" && !j.csi_survey_id && <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600 gap-1" onClick={() => openCSI(j)} data-testid={`csi-jc-${j.job_card_number}`}><Star className="w-3 h-3" /> CSI</Button>}
                  {j.csi_survey_id && <span className="text-[10px] font-mono uppercase text-emerald-600">✓ CSI</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">New Job Card</SheetTitle>
            <SheetDescription className="sr-only">Service intake — complaints, parts, labour.</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer name *</Label><Input value={form.customer.name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} className="rounded-sm mt-1" data-testid="jc-cust-name" /></div>
            <div><Label>Mobile</Label><Input value={form.customer.mobile} onChange={(e) => setForm({ ...form, customer: { ...form.customer, mobile: e.target.value } })} className="rounded-sm mt-1 font-mono" data-testid="jc-cust-mobile" /></div>
            <div><Label>VIN *</Label><Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="jc-vin" /></div>
            <div><Label>Service type</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="jc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Service date</Label><Input type="date" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Odometer in (km)</Label><Input type="number" value={form.odometer_in} onChange={(e) => setForm({ ...form, odometer_in: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
          </div>
          <div>
            <Label>Complaints</Label>
            {form.complaints.map((c, i) => (
              <div key={i} className="flex gap-2 mt-1">
                <Input value={c} onChange={(e) => setForm({ ...form, complaints: form.complaints.map((x, j) => j === i ? e.target.value : x) })} className="rounded-sm" placeholder={`Complaint ${i + 1}`} />
                {form.complaints.length > 1 && <button onClick={() => setForm({ ...form, complaints: form.complaints.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-1 gap-1" onClick={() => setForm({ ...form, complaints: [...form.complaints, ""] })}><Plus className="w-3.5 h-3.5" /> Complaint</Button>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Parts used</div>
            {form.parts_used.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`jc-part-${i}`}>
                <Input value={p.name || ""} onChange={(e) => setForm({ ...form, parts_used: form.parts_used.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} className="col-span-6 rounded-sm h-8 text-xs" placeholder="Part name" />
                <Input type="number" value={p.qty || ""} onChange={(e) => setForm({ ...form, parts_used: form.parts_used.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) })} className="col-span-2 rounded-sm h-8 text-right text-xs" placeholder="Qty" />
                <Input type="number" value={p.unit_price || ""} onChange={(e) => setForm({ ...form, parts_used: form.parts_used.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x) })} className="col-span-3 rounded-sm h-8 text-right text-xs" placeholder="Rate" />
                <button onClick={() => setForm({ ...form, parts_used: form.parts_used.filter((_, j) => j !== i) })} className="col-span-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-1 gap-1" onClick={() => setForm({ ...form, parts_used: [...form.parts_used, { name: "", qty: 1, unit_price: 0, hsn_code: "", gst_rate: 28 }] })}><Plus className="w-3.5 h-3.5" /> Part</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Labour charges</Label><Input type="number" value={form.labour_charges} onChange={(e) => setForm({ ...form, labour_charges: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="jc-labour" /></div>
            <div className="text-right text-sm mt-4">
              <div className="text-muted-foreground text-xs">Parts total {fmtINR(partsTotal)}</div>
              <div className="font-heading text-xl tabular-nums" data-testid="jc-total">{fmtINR(totalAmount)}</div>
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="jc-save">Open Job Card</Button>
        </SheetContent>
      </Sheet>

      <Sheet open={!!csiOpen} onOpenChange={(v) => !v && setCsiOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Customer Satisfaction Survey</SheetTitle>
            <SheetDescription className="sr-only">1-5 CSI score and 0-10 NPS score.</SheetDescription>
          </SheetHeader>
          {csiOpen && <>
            <div className="text-xs text-muted-foreground">{csiOpen.job_card_number} · {csiOpen.customer?.name}</div>
            <div>
              <Label>How would you rate your experience? (1-5) *</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setCsiForm({ ...csiForm, csi_score: n })} data-testid={`csi-star-${n}`}
                          className={`w-10 h-10 border rounded-sm flex items-center justify-center transition-colors ${csiForm.csi_score >= n ? "bg-amber-400 text-white border-amber-400" : "hover:border-amber-400"}`}>
                    <Star className={`w-5 h-5 ${csiForm.csi_score >= n ? "fill-current" : ""}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Would you recommend us? (0-10 NPS)</Label>
              <Input type="number" min="0" max="10" value={csiForm.nps_score} onChange={(e) => setCsiForm({ ...csiForm, nps_score: Number(e.target.value) })} className="rounded-sm mt-1 w-24" data-testid="csi-nps" />
            </div>
            <div><Label>Feedback</Label><Textarea value={csiForm.feedback} onChange={(e) => setCsiForm({ ...csiForm, feedback: e.target.value })} className="rounded-sm mt-1" rows={4} /></div>
            <Button onClick={runCSI} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="csi-save">Submit CSI</Button>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
